import type { FormSchemaResolved } from '#layers/block-engine';
import type { OAuth2Config, OAuth2FormValues } from '../protocols/oauth2';
import { AuthProtocol, defineAuthProvider } from '../../../types/auth-providers/define';
import { oauth2 } from '../protocols/oauth2';
import {
	applyOAuth2PresetOptions,
	normalizeDomain,
	OIDC_CLAIM_MAPPINGS,
	OIDC_SCOPES,
	promoteOAuth2PresetFields,
} from './oauth2-preset';

interface Auth0FormValues extends Omit<OAuth2FormValues, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	authorizationEndpoint: string;
}

const AUTH0_DEFAULTS = {
	'scopes': OIDC_SCOPES,
	'claimMappings.sub': OIDC_CLAIM_MAPPINGS.sub,
	'claimMappings.email': OIDC_CLAIM_MAPPINGS.email,
} as const;

const AUTH0_REGULAR_WEB_APP_URL = 'https://auth0.com/docs/get-started/auth0-overview/create-applications/regular-web-apps';

const AUTH0_DESCRIPTIONS: Record<string, string> = {
	authorizationEndpoint: '$t:sso_providers.auth0.domain_hint',
	clientId: '$t:sso_providers.auth0.client_id_hint',
	clientSecret: '$t:sso_providers.auth0.client_secret_hint',
};

function auth0Endpoints(domainInput: string): Pick<OAuth2Config, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	const domain = normalizeDomain(domainInput, '.auth0.com');

	return {
		authorizationEndpoint: `https://${domain}/authorize`,
		tokenEndpoint: `https://${domain}/oauth/token`,
		userinfoEndpoint: `https://${domain}/userinfo`,
	};
}

function parseAuth0Domain(config: unknown): Partial<Auth0FormValues> {
	const parsed = oauth2.parseInitialValues?.(config as OAuth2Config) ?? {};
	const authEndpoint = (config as { authorizationEndpoint?: unknown })?.authorizationEndpoint;

	if (typeof authEndpoint !== 'string') return parsed;

	try {
		return {
			...parsed,
			authorizationEndpoint: new URL(authEndpoint).hostname,
		};
	}
	catch {
		// Malformed URL in saved config — fall back to base parse so the form
		// renders the raw value rather than crashing on edit.
		return parsed;
	}
}

function applyAuth0Defaults(items: FormSchemaResolved<any>): FormSchemaResolved<any> {
	return promoteOAuth2PresetFields(applyOAuth2PresetOptions(items, {
		defaults: AUTH0_DEFAULTS,
		descriptions: AUTH0_DESCRIPTIONS,
		omitFieldNames: ['tokenEndpoint', 'userinfoEndpoint'],
		omitSectionLabels: ['$t:sso_providers.oauth2.section.endpoints'],
		fieldOverrides: {
			authorizationEndpoint: {
				label: '$t:sso_providers.auth0.domain_label',
				required: true,
				props: { placeholder: '$t:sso_providers.auth0.domain_placeholder' },
				default: () => '',
			},
		},
	}), ['authorizationEndpoint', 'clientId', 'clientSecret']);
}

export const auth0 = defineAuthProvider<typeof oauth2, OAuth2Config, Auth0FormValues>({
	id: 'auth0',
	name: '$t:sso_providers.auth0.name',
	description: '$t:sso_providers.auth0.description',
	protocol: AuthProtocol.OAuth2,
	icon: {
		default: 'logos:auth0-icon',
		dark: 'simple-icons:auth0',
	},
	docsUrl: AUTH0_REGULAR_WEB_APP_URL,
	walkthroughStepKeys: [
		{
			key: 'sso_providers.auth0.walkthrough.create_app',
			href: AUTH0_REGULAR_WEB_APP_URL,
		},
		'sso_providers.auth0.walkthrough.redirect',
		'sso_providers.auth0.walkthrough.credentials',
	],
	extends: oauth2,
	options: ({ form, context }) =>
		form(applyAuth0Defaults(context.base?.resolvedOptions ?? []) as never),
	transformOptions: (form): OAuth2Config =>
		oauth2.transformOptions!({
			...form,
			...auth0Endpoints(form.authorizationEndpoint),
		}),
	parseInitialValues: parseAuth0Domain,
});
